// Atlas Dashboard — Analytics Page
// Shows aggregate metrics, trends, and best sending times.

import { useState } from "react";
import { BarChart3, TrendingUp, Info } from "lucide-react";
import { useAnalytics } from "../hooks/useApi";
import { formatPercent, formatNumber, formatDuration } from "../lib/format";

export function AnalyticsPage() {
  const [range, setRange] = useState("30d");
  const { data, loading, error } = useAnalytics(range);

  if (loading) return <div style={{ padding: "48px", textAlign: "center", color: "var(--color-text-tertiary)" }}>Loading analytics...</div>;
  if (error) return <div style={{ padding: "48px", textAlign: "center", color: "#DC2626" }}>{error}</div>;
  if (!data) return null;

  const m = data.metrics;

  return (
    <div>
      {/* Range selector */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
        {["7d", "30d", "90d"].map((r) => (
          <button key={r} onClick={() => setRange(r)} style={{ padding: "6px 16px", borderRadius: "6px", border: `1px solid ${range === r ? "#2563EB" : "var(--color-border)"}`, backgroundColor: range === r ? "#EFF6FF" : "transparent", color: range === r ? "#2563EB" : "var(--color-text-secondary)", fontSize: "13px", fontWeight: range === r ? 600 : 400, cursor: "pointer" }}>
            {r === "7d" ? "Last 7 days" : r === "30d" ? "Last 30 days" : "Last 90 days"}
          </button>
        ))}
      </div>

      {/* Metric cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "32px" }}>
        <MetricCard label="Emails Sent" value={formatNumber(m.emailsSent)} />
        <MetricCard label="Open Rate" value={formatPercent(m.openRate)} note="May be understated if recipients block images" />
        <MetricCard label="Click Rate" value={formatPercent(m.clickRate)} />
        <MetricCard label="Reply Rate" value={formatPercent(m.replyRate)} note="Based on replies detected in your Gmail" />
        <MetricCard label="Avg Reply Time" value={formatDuration(m.avgReplyTimeMinutes)} />
        <MetricCard label="Engagement Rate" value={formatPercent(m.engagementRate)} note="Most inclusive — counts any engagement signal" />
      </div>

      {/* Honest limitation note */}
      <div style={{ display: "flex", gap: "8px", padding: "12px 16px", backgroundColor: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "8px", marginBottom: "32px", fontSize: "13px", color: "#92400E" }}>
        <Info size={16} style={{ flexShrink: 0, marginTop: "2px" }} />
        <span>{data.limitationNote}</span>
      </div>

      {/* Trend chart (simple bar chart of daily stats) */}
      {data.dailyStats.length > 0 && (
        <div style={{ backgroundColor: "var(--color-card)", borderRadius: "8px", border: "1px solid var(--color-border)", padding: "24px", marginBottom: "24px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>Emails Sent Over Time</h2>
          <SimpleBarChart data={data.dailyStats.map((d) => ({ label: d.date.substring(5), value: d.emailsSent }))} />
        </div>
      )}

      {data.dailyStats.length === 0 && (
        <div style={{ padding: "48px", textAlign: "center", backgroundColor: "var(--color-card)", borderRadius: "8px", border: "1px solid var(--color-border)" }}>
          <BarChart3 size={48} style={{ color: "var(--color-text-tertiary)", margin: "0 auto 16px" }} />
          <p style={{ color: "var(--color-text-secondary)", fontSize: "14px" }}>
            Not enough data yet. Analytics will appear once you've sent some tracked emails.
          </p>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div style={{ backgroundColor: "var(--color-card)", borderRadius: "8px", border: "1px solid var(--color-border)", padding: "20px" }}>
      <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: "8px" }}>{label}</div>
      <div style={{ fontSize: "28px", fontWeight: 700, color: "var(--color-text-primary)" }}>{value}</div>
      {note && <div style={{ fontSize: "12px", color: "var(--color-text-tertiary)", marginTop: "4px" }}>{note}</div>}
    </div>
  );
}

// Simple bar chart using divs (no chart library needed for this basic visualization)
function SimpleBarChart({ data }: { data: Array<{ label: string; value: number }> }) {
  const maxValue = Math.max(...data.map((d) => d.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "4px", height: "120px", overflowX: "auto", paddingBottom: "20px" }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "1", minWidth: "40px" }}>
          <div
            style={{
              width: "100%",
              height: `${(d.value / maxValue) * 100}%`,
              backgroundColor: "#2563EB",
              borderRadius: "4px 4px 0 0",
              minHeight: d.value > 0 ? "4px" : "0",
            }}
            title={`${d.value} emails`}
          />
          <div style={{ fontSize: "10px", color: "var(--color-text-tertiary)", marginTop: "4px", whiteSpace: "nowrap" as const }}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}
